// Shared markdown display utilities used by EmailMessage and convertEntryToMessage.

// Close unclosed markdown formatting pairs after truncation so
// MarkdownRenderer can still parse partial italic/bold/strikethrough.
export const closeMarkdownPairs = (text: string): string => {
  const pairs = ['**', '*', '~~'];
  let result = text;
  for (const marker of pairs) {
    const count = (result.split(marker).length - 1);
    if (count % 2 !== 0) {
      result += marker;
    }
  }
  return result;
};

// Auto-link bare URLs in markdown so they become clickable in MarkdownRenderer.
export const autoLinkMarkdownUrls = (markdown: string): string =>
  markdown.replace(
    /(\[|\]\(|<)?(https?:\/\/[^\s<>)"\]]+)/g,
    (match, prefix, url) => {
      if (prefix) return match;
      return `[${url}](${url})`;
    },
  );

// In standard markdown, a single \n is a soft break (rendered as space).
// Ensure every newline creates a visible paragraph break for MarkdownRenderer.
export const ensureParagraphBreaks = (md: string): string =>
  md.replace(/\n/g, '\n\n').replace(/\n{3,}/g, '\n\n');

// Styling overrides for MarkdownRenderer to match message text colors.
// NOTE: elementClassNames merges with (not replaces) defaults from core-components.
// Default dark mode colors: paragraphs/lists=gray-400, headings=white, links=blue-400.
// Only override light mode values where needed; avoid conflicting dark: classes.
export const MARKDOWN_ELEMENT_CLASSES = {
  p: '!text-gray-800 dark:!text-gray-200',
  strong: '!text-gray-800 dark:!text-gray-200',
  em: '!text-gray-800 dark:!text-gray-200',
  ul: '!text-gray-800 dark:!text-gray-200',
  ol: '!text-gray-800 dark:!text-gray-200',
  li: '!text-gray-800 dark:!text-gray-200',
  h1: '!text-gray-800 dark:!text-gray-200',
  h2: '!text-gray-800 dark:!text-gray-200',
  h3: '!text-gray-800 dark:!text-gray-200',
  a: '!font-normal',
  blockquote: '!text-gray-600 dark:!text-gray-400 !border-gray-300 dark:!border-gray-600',
  code: '!text-gray-800 dark:!text-gray-200 !bg-gray-100 dark:!bg-gray-700',
};

// Wrapper classes to fix list marker colors. Tailwind's !marker: variant doesn't generate CSS,
// so we use arbitrary selectors on a parent container to override core-components defaults.
export const MARKDOWN_WRAPPER_CLASSES = [
  '[&_ul]:!list-disc [&_ol]:!list-decimal',
  '[&_li::marker]:!text-gray-800 dark:[&_li::marker]:!text-gray-200',
  '[&_li::marker]:!font-normal',
].join(' ');
